import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { usePlayer, PlayPauseIcon } from "./PlayerContext";
import { formatPlays, timeAgo, isSignedIn } from "./fanHomeApi";

// The one card used by every music list. Three layouts, all with a 16:9 cover:
//  - "plain": cover, title and "Artist · Genre" (Search results).
//  - "grid" (default): cover with a small play button, then the artist's circle, the title, the artist and "plays · age".
//  - "compact": a fixed-width card for the sideways rows on Fan Home (Continue Playing, New from artists you follow).
// Clicking the cover or title plays (or pauses) the track through the shared player; the artist name opens the artist page.
const AVATAR_TINTS = ["#cfd8d2", "#d1e3d6", "#dcd5c4", "#e2cfd1", "#cfd8e6", "#d6cfe3"];

function Avatar({ track }) {
  const initial = (track.artist_name || "?").trim().charAt(0).toUpperCase();
  if (track.artist_picture_url) return <img src={track.artist_picture_url} alt="" loading="lazy" className="h-8 w-8 shrink-0 rounded-full object-cover" />;
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-gray-600" style={{ backgroundColor: AVATAR_TINTS[(track.artist_id || 0) % AVATAR_TINTS.length] }} aria-hidden="true">{initial}</span>
  );
}

export default function TrackCard({ track, badge, showPlays = false, showReleased = false, showProgress = false, startAt = 0, note, onPlay, queue, source, variant = "grid" }) {
  const { play, isCurrent, isUnavailable, playing, loading } = usePlayer();
  const current = isCurrent(track.id);
  const navigate = useNavigate();
  // Without the audio link (the server hides it from guests and from people without a membership) the card asks them to log in or
  // choose a plan instead of showing "Unavailable".
  const guest = !isSignedIn();
  const accessNeeded = !track.audio_url && !isUnavailable(track.id);
  const loginNeeded = accessNeeded && guest;
  const planNeeded = accessNeeded && !guest;
  const unavailable = !accessNeeded && (isUnavailable(track.id) || !track.audio_url);
  const active = current && playing;
  const compact = variant === "compact";
  const progress = showProgress && track.duration && track.position_seconds ? Math.min(100, (track.position_seconds / track.duration) * 100) : 0;

  const activate = () => {
    if (loginNeeded) { navigate("/login"); return; }
    if (planNeeded) { navigate("/fan-membership"); return; }
    if (unavailable) return;
    if (onPlay) onPlay(track);
    play(track, { startAt: startAt || track.position_seconds || 0, queue, source });
  };

  const released = showReleased ? timeAgo(track.released_at) : "";
  const plays = showPlays ? formatPlays(track.play_count) : "";
  const meta = [plays, released].filter(Boolean).join(" · ");
  const spinner = <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />;
  const artist = track.artist_slug
    ? <Link to={`/artists/${encodeURIComponent(track.artist_slug)}`} className="hover:underline">{track.artist_name}</Link>
    : track.artist_name;

  const cover = (
    <div className="relative aspect-video overflow-hidden rounded-xl bg-gray-100">
      <button
        type="button" onClick={activate} disabled={unavailable}
        aria-label={unavailable ? `${track.title} is unavailable` : active ? `Pause ${track.title}` : `Play ${track.title}`}
        className="absolute inset-0 h-full w-full text-left disabled:cursor-not-allowed"
      >
        {track.cover_url ? (
          <img src={track.cover_url} alt="" loading="lazy" className={`h-full w-full object-cover ${unavailable ? "opacity-40 grayscale" : ""}`} />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-300" aria-hidden="true">
            <svg viewBox="0 0 24 24" className="h-10 w-10" fill="currentColor"><path d="M9 18V5l12-2v13a3 3 0 1 1-2-2.83V6.3L11 7.5V18a3 3 0 1 1-2-2.83Z" /></svg>
          </div>
        )}
      </button>
      {badge && <span className="absolute left-2 top-2 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-semibold text-white">{badge}</span>}
      {accessNeeded ? (
        <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/60 py-1 text-center text-xs text-white">{loginNeeded ? "Log in to listen" : "Choose a plan to listen"}</span>
      ) : unavailable ? (
        <span className="absolute inset-x-0 bottom-0 bg-black/60 py-1 text-center text-xs text-white">Unavailable</span>
      ) : showProgress && compact ? (
        <span className="pointer-events-none absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow">
          {current && loading ? <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-900 border-t-transparent animate-spin" /> : <span className="scale-[0.7]"><PlayPauseIcon playing={active} /></span>}
        </span>
      ) : (
        <span className="pointer-events-none absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full text-white shadow" style={{ backgroundColor: ACCENT }}>
          {current && loading ? spinner : <span className="scale-[0.6]"><PlayPauseIcon playing={active} /></span>}
        </span>
      )}
      {progress > 0 && (
        <div className="absolute inset-x-0 bottom-0 h-1 bg-black/20" aria-hidden="true"><div className="h-full" style={{ width: `${progress}%`, backgroundColor: ACCENT }} /></div>
      )}
    </div>
  );

  const title = (
    <button type="button" onClick={activate} disabled={unavailable} className="block w-full truncate text-left text-[13px] font-semibold text-gray-900 hover:underline disabled:text-gray-400 disabled:no-underline" title={track.title}>
      {track.title}
    </button>
  );
  const details = <Link to={`/sound/${track.id}`} state={{ from: "fan-home" }} className="text-[11px] font-semibold hover:underline" style={{ color: ACCENT }}>Sound details</Link>;

  if (variant === "plain") {
    // Search results: cover, title (opens the sound page) and "Artist · Genre".
    return (
      <article className="min-w-0" aria-label={`${track.title} by ${track.artist_name || "unknown artist"}`}>
        {cover}
        <div className="mt-2 min-w-0">
          <Link to={`/sound/${track.id}`} state={{ from: "fan-search" }} className="block truncate text-[13px] font-semibold text-gray-900 hover:underline" title={track.title}>{track.title}</Link>
          <p className="truncate text-xs text-gray-500">{artist}{track.genre ? ` · ${track.genre}` : ""}</p>
        </div>
      </article>
    );
  }
  if (compact) {
    return (
      <article className="w-48 shrink-0" aria-label={`${track.title} by ${track.artist_name || "unknown artist"}`}>
        {cover}
        <div className="mt-2 min-w-0">
          {title}
          <p className="truncate text-xs text-gray-500">{artist}{released ? ` · ${released}` : ""}</p>
          {details}
        </div>
      </article>
    );
  }
  return (
    <article className="min-w-0" aria-label={`${track.title} by ${track.artist_name || "unknown artist"}`}>
      {cover}
      <div className="mt-2.5 flex gap-2.5">
        <Avatar track={track} />
        <div className="min-w-0 flex-1">
          {title}
          <p className="truncate text-xs text-gray-500">{artist}</p>
          {meta && <p className="truncate text-xs text-gray-500">{meta}</p>}
          {note && <p className="truncate text-xs text-gray-400">{note}</p>}
          {details}
        </div>
      </div>
    </article>
  );
}

export function TrackCardSkeleton({ compact = false }) {
  return (
    <div aria-hidden="true" className={compact ? "w-48 shrink-0" : undefined}>
      <div className="aspect-video rounded-xl bg-gray-100 animate-pulse" />
      <div className="mt-2 h-3.5 w-3/4 rounded bg-gray-100 animate-pulse" />
      <div className="mt-1.5 h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
    </div>
  );
}
